import { useEffect, useRef } from "react";

interface UsePageFocusOptions {
  onFocus?: () => void;
  minInactiveTime?: number; // minimum time in ms before triggering refresh
}

export function usePageFocus({ onFocus, minInactiveTime = 30000 }: UsePageFocusOptions = {}) {
  const lastActiveRef = useRef(Date.now());
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible
        const inactiveTime = Date.now() - lastActiveRef.current;
        if (inactiveTime > minInactiveTime && onFocus) {
          onFocus();
        }
      } else {
        // Page became hidden
        lastActiveRef.current = Date.now();
      }
    };

    const handleFocus = () => {
      const inactiveTime = Date.now() - lastActiveRef.current;
      if (inactiveTime > minInactiveTime && onFocus) {
        onFocus();
      }
    };

    const handleBlur = () => {
      lastActiveRef.current = Date.now();
    };

    // Reset timer on mount
    lastActiveRef.current = Date.now();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [onFocus, minInactiveTime]);
}